import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AdminBattlePassLevel,
  AdminBattlePassMission,
  AdminBattlePassReward,
  AdminCloneBattlePassPayload,
  AdminCreateSeasonPayload,
  AdminSeasonOverview,
  AdminSeasonRankingResponse,
  AdminSeasonReward,
} from '../models/season-admin';

@Injectable({
  providedIn: 'root',
})
export class SeasonAdminService {
  private readonly baseUrl = `${environment.apiUrl}/seasons/`;

  constructor(private http: HttpClient) {}

  list(params?: { search?: string; status?: string }): Observable<AdminSeasonOverview[]> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    return this.http.get<AdminSeasonOverview[]>(`${this.baseUrl}admin/list/`, {
      params: httpParams,
    });
  }

  templates(excludeId?: number): Observable<AdminSeasonOverview[]> {
    let httpParams = new HttpParams();
    if (excludeId) {
      httpParams = httpParams.set('exclude_id', String(excludeId));
    }
    return this.http.get<AdminSeasonOverview[]>(`${this.baseUrl}admin/templates/`, {
      params: httpParams,
    });
  }

  createWithTemplate(payload: AdminCreateSeasonPayload): Observable<AdminSeasonOverview> {
    return this.http.post<AdminSeasonOverview>(
      `${this.baseUrl}admin/create-with-template/`,
      payload
    );
  }

  detail(seasonId: number): Observable<AdminSeasonOverview> {
    return this.http.get<AdminSeasonOverview>(`${this.baseUrl}${seasonId}/admin/detail/`);
  }

  manageSeason(
    seasonId: number,
    payload: { name: string; start_date: string; end_date: string }
  ): Observable<AdminSeasonOverview> {
    return this.http.patch<AdminSeasonOverview>(
      `${this.baseUrl}${seasonId}/admin/manage-season/`,
      payload
    );
  }

  manageBattlePass(seasonId: number, payload: FormData): Observable<any> {
    return this.http.post(`${this.baseUrl}${seasonId}/admin/manage-pass/`, payload);
  }

  cloneBattlePass(
    seasonId: number,
    payload: AdminCloneBattlePassPayload
  ): Observable<AdminSeasonOverview> {
    return this.http.post<AdminSeasonOverview>(
      `${this.baseUrl}${seasonId}/admin/clone-from-template/`,
      payload
    );
  }

  listSeasonRewards(seasonId: number): Observable<AdminSeasonReward[]> {
    return this.http.get<AdminSeasonReward[]>(
      `${this.baseUrl}${seasonId}/admin/season-rewards/`
    );
  }

  createSeasonReward(
    seasonId: number,
    payload: Omit<AdminSeasonReward, 'id'>
  ): Observable<AdminSeasonReward> {
    return this.http.post<AdminSeasonReward>(
      `${this.baseUrl}${seasonId}/admin/season-rewards/`,
      payload
    );
  }

  updateSeasonReward(
    seasonId: number,
    rewardId: number,
    payload: Omit<AdminSeasonReward, 'id'>
  ): Observable<AdminSeasonReward> {
    return this.http.patch<AdminSeasonReward>(
      `${this.baseUrl}${seasonId}/admin/season-rewards/${rewardId}/`,
      payload
    );
  }

  deleteSeasonReward(seasonId: number, rewardId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${seasonId}/admin/season-rewards/${rewardId}/`);
  }

  listLevels(seasonId: number): Observable<AdminBattlePassLevel[]> {
    return this.http.get<AdminBattlePassLevel[]>(`${this.baseUrl}${seasonId}/admin/levels/`);
  }

  createLevel(
    seasonId: number,
    payload: Omit<AdminBattlePassLevel, 'id'>
  ): Observable<AdminBattlePassLevel> {
    return this.http.post<AdminBattlePassLevel>(
      `${this.baseUrl}${seasonId}/admin/levels/`,
      payload
    );
  }

  updateLevel(
    seasonId: number,
    levelId: number,
    payload: Omit<AdminBattlePassLevel, 'id'>
  ): Observable<AdminBattlePassLevel> {
    return this.http.patch<AdminBattlePassLevel>(
      `${this.baseUrl}${seasonId}/admin/levels/${levelId}/`,
      payload
    );
  }

  deleteLevel(seasonId: number, levelId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${seasonId}/admin/levels/${levelId}/`);
  }

  listMissions(seasonId: number): Observable<AdminBattlePassMission[]> {
    return this.http.get<AdminBattlePassMission[]>(
      `${this.baseUrl}${seasonId}/admin/missions/`
    );
  }

  createMission(
    seasonId: number,
    payload: Omit<AdminBattlePassMission, 'id'>
  ): Observable<AdminBattlePassMission> {
    return this.http.post<AdminBattlePassMission>(
      `${this.baseUrl}${seasonId}/admin/missions/`,
      payload
    );
  }

  updateMission(
    seasonId: number,
    missionId: number,
    payload: Omit<AdminBattlePassMission, 'id'>
  ): Observable<AdminBattlePassMission> {
    return this.http.patch<AdminBattlePassMission>(
      `${this.baseUrl}${seasonId}/admin/missions/${missionId}/`,
      payload
    );
  }

  deleteMission(seasonId: number, missionId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${seasonId}/admin/missions/${missionId}/`);
  }

  listPassRewards(seasonId: number): Observable<AdminBattlePassReward[]> {
    return this.http.get<AdminBattlePassReward[]>(
      `${this.baseUrl}${seasonId}/admin/rewards/`
    );
  }

  createPassReward(
    seasonId: number,
    payload: {
      level: number;
      reward_type: string;
      category: string;
      reward_amount: number;
      hero_id?: number | null;
    }
  ): Observable<AdminBattlePassReward> {
    return this.http.post<AdminBattlePassReward>(
      `${this.baseUrl}${seasonId}/admin/rewards/`,
      payload
    );
  }

  updatePassReward(
    seasonId: number,
    rewardId: number,
    payload: {
      level: number;
      reward_type: string;
      category: string;
      reward_amount: number;
      hero_id?: number | null;
    }
  ): Observable<AdminBattlePassReward> {
    return this.http.patch<AdminBattlePassReward>(
      `${this.baseUrl}${seasonId}/admin/rewards/${rewardId}/`,
      payload
    );
  }

  deletePassReward(seasonId: number, rewardId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${seasonId}/admin/rewards/${rewardId}/`);
  }

  listRanking(
    seasonId: number,
    params?: {
      search?: string;
      position_from?: number;
      position_to?: number;
      page?: number;
    }
  ): Observable<AdminSeasonRankingResponse> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    return this.http.get<AdminSeasonRankingResponse>(`${this.baseUrl}${seasonId}/admin/ranking/`, {
      params: httpParams,
    });
  }
}
